import { useContext, useState, useCallback } from 'react';

import { MapPointer, getPointersAndClusters } from '@api/atlas';

import { ClusterPointer } from '@api/types/shared';

import { useDebounceEffect } from '@utils/use-debounce-effect';

import { MapContext } from '@contexts/map-context/map-context';

import { AtlasFiltersContext } from '@atlas/contexts/filters';
import { AtlasEventsContext } from '@atlas/contexts/events';

export const useAtlasMapData = () => {
  // contexts
  const { emit } = useContext(AtlasEventsContext);
  const { filters } = useContext(AtlasFiltersContext);
  const { bounds, loaded, zoom } = useContext(MapContext);

  // Pointers fetching
  const [clusterPointers, setClusterPointers] = useState<ClusterPointer[]>([]);
  const [pointers, setPointers] = useState<MapPointer[]>([]);

  // fetch pointers and clustered pointers initially and on bounds or filters change
  const debouncedFn = useCallback(
    (deps: [typeof bounds, typeof filters, typeof loaded, typeof zoom]) => {
      if (!deps) return;
      const [bounds, filters, loaded, zoom] = deps;
      if (!loaded) return;

      let canceled = false;

      getPointersAndClusters({
        // Backend expects [Lat, Long] points, but leaflet uses [Long, Lat]
        bounds: {
          southWestLatitude: bounds[0][1],
          southWestLongitude: bounds[0][0],
          northEastLatitude: bounds[1][1],
          northEastLongitude: bounds[1][0],
        },
        types: JSON.stringify(filters.types),
        areaRange: JSON.stringify(filters.area),
        pricePerSquareMRange: JSON.stringify(filters.pricePerSquareM),
        soldAfter: filters.date
          ? JSON.stringify(filters.date.soldAfter)
          : undefined,
        zoom: zoom,
      }).then((result) => {
        if (canceled) return;
        setPointers(result.data.pointers);
        setClusterPointers(result.data.clusterPointers);
      });
      return () => {
        canceled = true;
      };
    },
    [emit, setPointers, setClusterPointers],
  );
  useDebounceEffect<
    [typeof bounds, typeof filters, typeof loaded, typeof zoom]
  >(debouncedFn, [bounds, filters, loaded, zoom], 500);

  return { clusterPointers, pointers };
};
